import { ChangeDetectionStrategy, Component, DestroyRef, effect, OnInit, TemplateRef, untracked, viewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { features } from '@consts/text.const';
import { PreloaderColor } from '@enums/shared.enum';
import { SharedEffects } from '@effects/shared.effects';
import { syncQueryParam } from '../../../../helpers/sync-query-param.helper';
import { AppState } from '../../../../store/app.state';
import { DatabaseTagsModel } from '@models/database-tags.model';
import { DatabaseTagsSelectors } from '@states/database-tags/database-tags.selector-types';
import { DatabaseTagsActions } from '@states/database-tags/database-tags.action.types';
import { LetDirective } from '@ngrx/component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiNoDataComponent } from '@ui';
import { UiPreloaderComponent } from 'src/app/shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiSearchComponent } from 'src/app/shared/ui-kit/ui-search/ui-search.component';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createTagsTableConfig, TagCellContext } from './tags-table.util';
import { computed } from '@angular/core';
import { filter, Observable, tap } from 'rxjs';
import { MenuActions } from '@states/menu/menu.action-types';
import { MenuKey } from '@enums/menu.enum';
import { SharedActions } from '@states/shared/shared.action-types';
import { ConfirmDialogModel } from '@ui';
import { ConfirmModalModel } from 'src/app/services/confirm-dialogs/confirm-modal.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ofType } from '@ngrx/effects';
import { EditTagDialogComponent } from './dialogs/edit-tag-dialog/edit-tag-dialog.component';
import { CreateTagDialogComponent } from './dialogs/create-tag-dialog/create-tag-dialog.component';
import { Router } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { RouteModalService, UiButtonComponent } from '@ui';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-tags',
  imports: [
    LetDirective,
    TranslatePipe,
    UiSearchComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiDatasetTableComponent,
    NgIf,
    UiButtonComponent,
  ],
  templateUrl: './tags.component.html',
  styleUrl: './tags.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagsComponent implements OnInit {
  public loaderColor = PreloaderColor;
  public noResults = features.tags.noResults;
  public defaultTagColor = DatabaseTagsModel.defaultTagColor;

  public selectDatabaseTags$: Observable<DatabaseTagsModel.DatabaseTagDocument[]> = this.store$.pipe(select(DatabaseTagsSelectors.selectFilteredDatabaseTags));
  public setIsLoading$: Observable<boolean> = this.store$.pipe(select(DatabaseTagsSelectors.selectIsLoading));

  public itemSize: number = 72;

  protected search = syncQueryParam<string>('search');
  protected readonly nameCellTemplate = viewChild<TemplateRef<TagCellContext>>('nameCell');
  protected readonly isShownInLeftPanelCellTemplate = viewChild<TemplateRef<TagCellContext>>('isShownInLeftPanelCell');

  public readonly tableConfig = computed(() => {
    const nameTemplate = this.nameCellTemplate();
    const isShownInLeftPanelCellTemplate = this.isShownInLeftPanelCellTemplate();

    if (!nameTemplate) return null;

    return createTagsTableConfig({
      nameCellTemplate: nameTemplate,
      isShownInLeftPanelCellTemplate: isShownInLeftPanelCellTemplate,
      defaultTagColor: this.defaultTagColor,
      onRowClick: (row) => this.navigateToTagEntities(row._id),
    });
  });

  constructor(
    private store$: Store<AppState>,
    private sharedEffects$: SharedEffects,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
    private translateService: TranslateService,
    private router: Router,
    private modalService: RouteModalService,
  ) {
    effect(() => {
      const query = this.search();
      untracked(() => {
        if (query !== undefined) {
          this.store$.dispatch(DatabaseTagsActions.setQuery({ query }));
        }
      });
    });

  }

  public ngOnInit(): void {
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.tags }));

    this.sharedEffects$.confirmation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(SharedActions.showConfirmModalResultConfirm),
        filter(
          (x) => x.params['type'] === ConfirmModalModel.Action.removeDatabaseTag
        ),
        tap((x) => {
          this.store$.dispatch(DatabaseTagsActions.removeDatabaseTag({ id: x.params['id'] }));
        }),
      )
      .subscribe();

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.createTag,
          open: () => this.openCreateDialog(),
        },
        {
          queryParam: openDialogByParams.editTag,
          open: (params) => {
            const { id, name, color, isShownInLeftPanel } = params;
            return this.openEditDialog(id, name, color, isShownInLeftPanel === "true");
          },
        },
      ],
      this.destroyRef,
    );
  }

  public delete(id: string, name: string): void {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          title: this.translateService.instant('DATABASE_TAGS.DELETE_MODAL.TITLE'),
          msg: this.translateService.instant('DATABASE_TAGS.DELETE_MODAL.MESSAGE', { name: `"${name}"` }),
          confirm: this.translateService.instant('COMMON.DELETE'),
          disableClose: true,
          params: {
            type: ConfirmModalModel.Action.removeDatabaseTag,
            id: id,
          },
        },
      }),
    );
  }

  public openEditDialog(id: string, name: string, color: string, isShownInLeftPanel: boolean): MatDialogRef<EditTagDialogComponent, any> {
    return this.dialog.open(EditTagDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '472px',
      data: {
        id,
        name,
        color: color ?? this.defaultTagColor,
        isShownInLeftPanel: isShownInLeftPanel,
      },
    });
  }

  public openCreateDialog(): MatDialogRef<CreateTagDialogComponent, any> {
    return this.dialog.open(CreateTagDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '472px',
    });
  }

  public edit(id: string, name: string, color: string, isShownInLeftPanel: boolean): void {
    this.modalService.openModal(openDialogByParams.editTag, { id, name, color, isShownInLeftPanel: isShownInLeftPanel == undefined ? true : isShownInLeftPanel });
  }

  public create(): void {
    this.modalService.openModal(openDialogByParams.createTag);
  }

  public navigateToTagEntities(tagId: string): void {
    this.router.navigate([`/${routerSegments.database}/${routerSegments.tags}`, tagId]);
  }

  public onRowAction(event: {
    actionId: string;
    row: DatabaseTagsModel.DatabaseTagDocument;
  }): void {
    if (event.actionId === 'edit') {
      this.edit(event.row._id, event.row.name, event.row.color, event.row.isShownInLeftPanel);
    } else if (event.actionId === 'delete') {
      this.delete(event.row._id, event.row.name);
    }
  }
}
